import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { 
  X, List, Plus, Check, Lock, Users, Globe, 
  Star, Loader2 
} from 'lucide-react';

function AddToListModal({ barId, barName, onClose }) {
  const { lists, addBarToList, removeBarFromList, createList } = useApp();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  
  const [newList, setNewList] = useState({
    title: '',
    description: '',
    privacy: 'private'
  });

  const isBarInList = (list) => {
    return list.list_bars?.some(lb => lb.bar_id === barId) || false;
  };

  const handleToggleList = async (list) => {
    setActionLoading(list.id);
    setError('');
    
    try {
      if (isBarInList(list)) {
        await removeBarFromList(list.id, barId);
      } else {
        await addBarToList(list.id, barId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateAndAdd = async (e) => {
    e.preventDefault();
    if (!newList.title.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const createdList = await createList(
        newList.title.trim(), 
        newList.description.trim(), 
        newList.privacy
      );
      
      // Add bar to the new list
      await addBarToList(createdList.id, barId);
      
      setNewList({ title: '', description: '', privacy: 'private' });
      setShowCreateForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'public': return <Globe className="w-4 h-4 text-green-400" />;
      case 'friends': return <Users className="w-4 h-4 text-blue-400" />;
      default: return <Lock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-dive-dark border border-dive-gray rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dive-gray">
          <div>
            <h2 className="text-lg font-display text-white">Add to List</h2>
            <p className="text-gray-400 text-sm truncate">{barName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 bg-red-500/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Lists */}
        <div className="flex-1 overflow-y-auto p-4">
          {lists.length === 0 && !showCreateForm ? (
            <div className="text-center py-8">
              <List className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm mb-4">No lists yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {lists.map(list => {
                const inList = isBarInList(list);
                const isLoading = actionLoading === list.id;
                
                return (
                  <button
                    key={list.id}
                    onClick={() => handleToggleList(list)}
                    disabled={isLoading}
                    className={`w-full p-3 rounded-lg border flex items-center gap-3 transition-colors ${
                      inList
                        ? 'border-dive-neon bg-dive-neon/10'
                        : 'border-dive-gray hover:border-gray-500'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                      inList ? 'bg-dive-neon' : 'bg-dive-gray'
                    }`}>
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                      ) : inList ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <Plus className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                    
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        {list.is_system && (
                          <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        )}
                        <span className="text-white text-sm">{list.title}</span>
                      </div>
                      <span className="text-gray-500 text-xs">
                        {list.list_bars?.length || 0} bars
                      </span>
                    </div>
                    
                    {getPrivacyIcon(list.privacy)}
                  </button>
                );
              })}
            </div>
          )}

          {/* Create new list form */}
          {showCreateForm && (
            <form onSubmit={handleCreateAndAdd} className="mt-4 p-4 bg-dive-darker rounded-lg border border-dive-gray">
              <h3 className="text-white text-sm font-medium mb-3">Create New List</h3>
              
              <input
                type="text"
                value={newList.title}
                onChange={(e) => setNewList(prev => ({ ...prev, title: e.target.value }))}
                placeholder="List name..."
                className="w-full mb-3"
                required
                autoFocus
              />

              <textarea
                value={newList.description}
                onChange={(e) => setNewList(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description (optional)"
                className="w-full h-16 resize-none mb-3"
              />

              <div className="flex gap-2 mb-4">
                {[
                  { value: 'private', icon: Lock },
                  { value: 'friends', icon: Users },
                  { value: 'public', icon: Globe }
                ].map(({ value, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setNewList(prev => ({ ...prev, privacy: value }))}
                    className={`flex-1 py-2 rounded-lg border flex items-center justify-center ${
                      newList.privacy === value
                        ? 'border-dive-neon bg-dive-neon/20'
                        : 'border-dive-gray'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${
                      newList.privacy === value ? 'text-dive-neon' : 'text-gray-400'
                    }`} />
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 py-2 text-gray-400 hover:text-white text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !newList.title.trim()}
                  className="flex-1 btn-primary py-2 text-sm"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    'Create & Add'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        {!showCreateForm && (
          <div className="p-4 border-t border-dive-gray">
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full py-3 border border-dashed border-dive-gray rounded-lg text-gray-400 hover:text-white hover:border-gray-500 flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create New List
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddToListModal;


